import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'

import { OutputType } from 'app/constants/outputType'
import { SERVICE_DATE_FORMAT } from 'app/constants/date'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { GroupModel } from 'app/models/groupModel'
import { isAdmin } from 'app/services/local/roleService'

import { getCompanyTypeIcon } from 'app/services/company/companyIconResolver'
import { AdminIcon } from '@/modules/shared/components'
import { Dropdown } from '@/app/features/company-selection/Dropdown'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminModal, AdminModalRow } from '../shared/AdminModal'

/** Sześć ostatnich okresów rozliczeniowych, licząc od bieżącego miesiąca. */
const historicalPeriods = (): Moment[] => {
  const firstOfMonth = moment().startOf('month')
  return [0, 1, 2, 3, 4, 5].map((offset) =>
    firstOfMonth.clone().add(-offset, 'months'),
  )
}

type Props = {
  source: number
  realIncome: boolean
  currentCompanyId: string
  companies: CompanyDetailsModel[]
  groups: GroupModel[]
  getCompanyDataReport: (
    companies: Array<string>,
    groups: Array<string>,
    months: Array<number>,
    year: number,
    outputType: OutputType,
    sortBy: number,
    onlyPro: boolean,
    onlyPackage: boolean,
    mergeSameCompanies: boolean,
    source: number,
    updatePaymentDate: boolean,
  ) => any
  getRealIncomeReport: (companies: Array<string>, period: Moment) => any
  getCompaniesForGroups: (
    ids: Array<string>,
    includeDeleted?: boolean,
  ) => Promise<Array<CompanyDetailsModel>>
  getGroups: (tenantId?: string) => Promise<Array<GroupModel>>
  onClose: (cancel: boolean) => void
  onFinish: () => void
}

/**
 * Wydruk danych do faktur / realnego przychodu - funkcyjny odpowiednik
 * `printout/invoiceDataModal.tsx`.
 */
export const InvoiceDataModal: React.FC<Props> = ({
  source,
  realIncome,
  currentCompanyId,
  companies,
  groups,
  getCompanyDataReport,
  getRealIncomeReport,
  getCompaniesForGroups,
  getGroups,
  onClose,
  onFinish,
}) => {
  const { t } = useLocalize()
  const periods = React.useMemo(historicalPeriods, [])

  const [selectedCompanies, setSelectedCompanies] = React.useState<string[]>([])
  const [selectedGroups, setSelectedGroups] = React.useState<string[]>([])
  const [period, setPeriod] = React.useState<Moment>(periods[0])

  React.useEffect(() => {
    getGroups(currentCompanyId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompanyId])

  // Firmy podrzędne wchodzą na listę razem z rodzicem; bez uprawnień admina tylko te
  // z bieżącego tenanta - dokładnie jak `constructCompanies()` w starej wersji.
  const allCompanies = React.useMemo(() => {
    const result: CompanyDetailsModel[] = []
    for (const company of companies) {
      result.push({ ...company })
      for (const child of company.childCompanies ?? []) {
        if (!isAdmin() && child.tenantId !== currentCompanyId) continue
        result.push({ ...child })
      }
    }
    return result
  }, [companies, currentCompanyId])

  // Stara wersja zaznaczała wszystko, ilekroć przyszła nowa lista firm.
  React.useEffect(() => {
    setSelectedCompanies(allCompanies.map((company) => String(company.id)))
  }, [allCompanies])

  return (
    <AdminModal
      title={t(
        realIncome ? 'invoiceDataModal.real-income-title' : 'invoiceDataModal.title',
      )}
      onClose={() => onClose(true)}
      onConfirm={async () => {
        onClose(false)
        if (realIncome) {
          await getRealIncomeReport(selectedCompanies, period)
        } else {
          await getCompanyDataReport(
            selectedCompanies,
            [],
            [moment().month() + 1],
            moment().year(),
            OutputType.Csv,
            0,
            false,
            false,
            false,
            source,
            false,
          )
        }
        onFinish()
      }}
    >
      <AdminModalRow label={t('audit-report.groups')}>
        <Dropdown
          compact
          multiple
          showSelectAll
          portalMenu
          placeholder={t('administrationPanel-users.choose-group')}
          options={groups.map((group) => ({
            value: String(group.id),
            label: group.name ?? '',
          }))}
          value={selectedGroups}
          onChange={(values: string[]) => {
            setSelectedGroups(values)
            getCompaniesForGroups(values)
          }}
        />
      </AdminModalRow>

      <AdminModalRow label={t('synchronization-report.companies')}>
        <Dropdown
          compact
          multiple
          showSelectAll
          portalMenu
          searchPlaceholder={t(
            'administrationPanel-users.filter-find-placeholder',
          )}
          placeholder={t('administrationPanel-users.filter-companies')}
          options={allCompanies.map((company) => ({
            value: String(company.id),
            label: company.companyName,
            content: (
              <span className="tw-flex tw-items-center tw-gap-2">
                <AdminIcon
                  name={getCompanyTypeIcon(
                    company.type,
                    company.accountingOnly,
                  )}
                  className="tw-h-4 tw-w-4"
                />
                <span className="tw-flex tw-flex-col">
                  <span>{company.companyName}</span>
                  {company.tenant != null && (
                    <span className="tw-text-xs tw-opacity-70">
                      {company.tenant.companyName}
                    </span>
                  )}
                </span>
              </span>
            ),
          }))}
          value={selectedCompanies}
          onChange={setSelectedCompanies}
        />
      </AdminModalRow>

      {realIncome && (
        <AdminModalRow label={t('synchronization-report.period')}>
          <Dropdown
            compact
          portalMenu
            placeholder={t('paymentCompanies.choose-period')}
            options={periods.map((item) => ({
              value: item.format(SERVICE_DATE_FORMAT),
              label: item.format(SERVICE_DATE_FORMAT),
            }))}
            value={period.format(SERVICE_DATE_FORMAT)}
            onChange={(value) => setPeriod(moment(value))}
          />
        </AdminModalRow>
      )}
    </AdminModal>
  )
}
